import { useResponsiveView } from "@gui/hooks/useResponsiveView";
import { resolveAsyncState } from "@gui/widgets/AsyncState/resolveAsyncState";
import type { PageLayoutProps, PageTopToolbarConfig } from "./PageLayout.types";
import type { PagePhoneActionsConfig } from "./PagePhoneActions";

/**
 * Where a page's commit actions are drawn. Desktop: as declared, the top toolbar's right end or the
 * bottom toolbar. Phone: both move to the bottom of the screen (PagePhoneActions), unless the top
 * toolbar keeps its own with `phonePlacement: "top"`. Neither shows while the page loads or after
 * its load failed: there is nothing on screen to commit.
 */
export function usePageLayoutViewModel<T extends string>(props: PageLayoutProps<T>) {
	const { isMobile } = useResponsiveView();
	const bodyState = props.async ? resolveAsyncState(props.async) : "ready";
	const commitShown = bodyState !== "loading" && bodyState !== "error";
	const top = props.topToolbar;
	const bottom = props.bottomToolbar;

	const topCommitMoves = isMobile && !bottom && top !== undefined && (top.phonePlacement ?? "bottom") === "bottom";
	const topToolbar: PageTopToolbarConfig | undefined = topCommitMoves
		? { ...top, endActions: undefined, customEnd: undefined, primaryAction: undefined }
		: top;

	let phoneActions: PagePhoneActionsConfig | undefined;
	if (isMobile && commitShown) {
		if (bottom) {
			phoneActions = { actions: bottom.actions ?? [], primaryAction: bottom.primaryAction };
		} else if (topCommitMoves && (top.primaryAction || top.customEnd || top.endActions?.length)) {
			phoneActions = { actions: top.endActions ?? [], custom: top.customEnd, primaryAction: top.primaryAction };
		}
	}

	return {
		topToolbar,
		phoneActions,
		showBottomToolbar: !isMobile && bottom !== undefined && commitShown,
	};
}
